import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download, AlertTriangle } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import { getAtsRecruitingReport, exportAtsRecruitingReport } from '../../services/atsReportService';
import { getDepartments } from '../../services/departmentService';
import api from '../../services/api';
import { parseTableParams } from '../../utils/Utils';
import { employeeDisplayName } from '../ats-requisitions/employeeOption';
import { REQUISITION_STATUSES, useAtsRecruitingReportColumns, otherCurrencyCountOf } from './config';

const Bar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const FieldBox = styled.div`
  width: 220px;
`;

const CurrencyWarn = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #8A5A00;
  background: #FFF7E6;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
`;

export default function AtsRecruitingReportPage() {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 25 });
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 25 });
  const [sort, setSort] = useState({});
  const [departmentId, setDepartmentId] = useState(null);
  const [status, setStatus] = useState(null);
  const [hiringManagerUserId, setHiringManagerUserId] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const columns = useAtsRecruitingReportColumns(data);

  // SPEC F3 — geçerli para biriminde OLMAYAN maliyet kayıtları toplamlara girmez.
  const otherCurrencyTotal = useMemo(
    () => data.reduce((s, r) => s + otherCurrencyCountOf(r), 0),
    [data],
  );

  const statusOptions = useMemo(
    () => REQUISITION_STATUSES.map((s) => ({ id: s, name: t(`ats.requisition.status.${s}`, { defaultValue: s }) })),
    [t],
  );

  // Sunucu taraflı süzgeçler — bracket-notation, AtsRequisitionController'daki kalıbın aynısı.
  // Client-side filtreleme YOK: her değişim yeni bir /v1/ats-recruiting-report isteği tetikler.
  const filterParams = useMemo(() => ({
    'departmentId[eq]': departmentId,
    'status[in]': status,
    'hiringManagerUserId[eq]': hiringManagerUserId,
    'approvedAt[gte]': dateFrom,
    'approvedAt[lte]': dateTo,
    ...sort,
  }), [departmentId, status, hiringManagerUserId, dateFrom, dateTo, sort]);

  const queryParams = useMemo(() => ({ ...filterParams, ...page }), [filterParams, page]);

  const fetchData = useCallback((params) => {
    setFetching(true);
    getAtsRecruitingReport(params)
      .then((res) => {
        // api.js GET'leri reject etmez — başarı sinyali res.data.status'tur.
        if (!res.data?.status) {
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: params.pageSize || 25 });
          return;
        }
        const result = res.data?.data;
        const items = result?.data || [];
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || 25,
        });
      })
      .catch(() => {
        setData([]);
        setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: params.pageSize || 25 });
      })
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleTableChange = useCallback((action, tableState) => {
    if (action === 'sort') {
      const { name: colName, direction } = tableState.sortOrder || {};
      if (!colName) { setSort({}); return; }
      const col = columns.find((c) => c.name === colName);
      const orderBy = col?.options?.sortKey || colName;
      setSort({ orderBy, orderDir: direction || 'asc' });
      return;
    }
    const parsed = parseTableParams(tableState, []);
    setPage({ pageNumber: parsed.pageNumber || 1, pageSize: parsed.pageSize || 25 });
  }, [columns]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportAtsRecruitingReport(filterParams);
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ats-ise-alim-raporu.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [filterParams]);

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await getDepartments(params);
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  // /v1/employees — ats-requisitions/config'in HM seçicisiyle AYNI kaynak.
  const searchHiringManagers = useCallback(async (query) => {
    try {
      const params = query ? { 'fullName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      const items = Array.isArray(result) ? result : (result?.data || []);
      return items.map((e) => ({ id: e.id, name: employeeDisplayName(e), profilePicture: e.profilePicture }));
    } catch { return []; }
  }, []);

  return (
    <>
      <Bar>
        <FieldBox>
          <AsyncGenericSelect
            compact
            label={t('atsReport.filterDepartment')}
            value={departmentId}
            onChange={setDepartmentId}
            onSearch={searchDepartments}
          />
        </FieldBox>
        <FieldBox>
          <GenericSelect
            size="small"
            options={statusOptions}
            value={status}
            onChange={(v) => setStatus(v || null)}
            placeholder={t('atsReport.filterStatus')}
          />
        </FieldBox>
        <FieldBox>
          <AsyncGenericSelect
            compact
            label={t('atsReport.filterHiringManager')}
            value={hiringManagerUserId}
            onChange={setHiringManagerUserId}
            onSearch={searchHiringManagers}
          />
        </FieldBox>
        <FieldBox>
          <DateTimePicker
            mode="date"
            label={t('atsReport.filterFrom')}
            value={dateFrom}
            onChange={(iso) => setDateFrom(iso || null)}
          />
        </FieldBox>
        <FieldBox>
          <DateTimePicker
            mode="date"
            label={t('atsReport.filterTo')}
            value={dateTo}
            onChange={(iso) => setDateTo(iso || null)}
          />
        </FieldBox>
      </Bar>

      {otherCurrencyTotal > 0 && (
        <CurrencyWarn role="status" data-role="ats-report-currency-warning">
          <AlertTriangle size={14} />
          {t('atsReport.otherCurrencyWarning', { count: otherCurrencyTotal })}
        </CurrencyWarn>
      )}

      <DataTable
        title={t('atsReport.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onTableChanged={handleTableChange}
        disableFilters
        toolbarActions={(
          // Tasarım dili: tablo üstü aksiyonlar ikon + tooltip'tir (metinli
          // düğme değil) — yerleşik indir/yazdır/kolon/filtre ikonlarının
          // yanında duruyor. Metin tooltip'e taşındı; görme engelli
          // kullanıcı için `aria-label` aynı metni taşır.
          <Tooltip text={t('atsReport.exportExcel')}>
            <IconButton
              type="button"
              onClick={handleExport}
              disabled={exporting}
              aria-label={t('atsReport.exportExcel')}
            >
              <Download size={16} />
            </IconButton>
          </Tooltip>
        )}
      />
    </>
  );
}
